import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import {
  Button,
  Spinner,
  StatusBadge,
  Input,
  Select,
  Textarea,
} from '../../components/design-system';
import { AdminPage } from './AdminPage';

interface FaqRow {
  id: string;
  question: string;
  answer: string;
  audience: 'traveler' | 'host' | 'both';
  sort_order: number;
  published: boolean;
  created_at: string;
}

interface FaqForm {
  question: string;
  answer: string;
  audience: 'traveler' | 'host' | 'both';
  sort_order: number;
  published: boolean;
}

const EMPTY_FORM: FaqForm = { question: '', answer: '', audience: 'both', sort_order: 0, published: true };

const AUDIENCE_LABELS: Record<string, string> = { traveler: 'Traveler', host: 'Host', both: 'Both' };
const AUDIENCE_COLORS: Record<string, string> = {
  traveler: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  host: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  both: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};

function FaqEditor({ faq, onSave, onCancel }: { faq: FaqForm & { id?: string }; onSave: (f: FaqForm, id?: string) => Promise<void>; onCancel: () => void }) {
  const [form, setForm] = useState<FaqForm>({ question: faq.question, answer: faq.answer, audience: faq.audience, sort_order: faq.sort_order, published: faq.published });
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    await onSave(form, faq.id);
    setSaving(false);
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="bg-app-bg rounded-xl p-4 space-y-3">
      <Input label="Question" value={form.question} onChange={(e) => setForm((p) => ({ ...p, question: e.target.value }))} required disabled={saving} />
      <Textarea label="Answer" value={form.answer} onChange={(e) => setForm((p) => ({ ...p, answer: e.target.value }))} required disabled={saving} rows={4} />
      <div className="flex flex-wrap gap-3 items-end">
        <Select
          label="Audience"
          value={form.audience}
          onChange={(e) => setForm((p) => ({ ...p, audience: e.target.value as FaqForm['audience'] }))}
          disabled={saving}
          options={[
            { value: 'both', label: 'Both' },
            { value: 'traveler', label: 'Traveler only' },
            { value: 'host', label: 'Host only' },
          ]}
        />
        <Input label="Sort order" type="number" value={String(form.sort_order)} onChange={(e) => setForm((p) => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} disabled={saving} className="w-24" />
        <label className="flex items-center gap-2 text-sm text-app-fg-2 pb-1">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm((p) => ({ ...p, published: e.target.checked }))} disabled={saving} className="rounded border-app-border text-gold" />
          Published
        </label>
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" type="submit" disabled={saving || !form.question.trim() || !form.answer.trim()}>
          {saving ? <Spinner size="sm" className="text-ink" /> : faq.id ? 'Update' : 'Create'}
        </Button>
        <Button size="sm" variant="ghost" type="button" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </form>
  );
}

export function AdminFAQ() {
  const { user } = useAuth();
  const [faqs, setFaqs] = useState<FaqRow[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  function fetchFaqs() {
    setDataLoading(true);
    void supabase
      .from('faqs')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setFaqs((data as FaqRow[]) ?? []);
        setDataLoading(false);
      });
  }

  useEffect(() => {
    if (!user) return;
    fetchFaqs();
  }, [user]);

  async function handleSave(form: FaqForm, id?: string) {
    const payload = { ...form, updated_at: new Date().toISOString() };
    if (id) {
      await supabase.from('faqs').update(payload).eq('id', id);
    } else {
      await supabase.from('faqs').insert(payload);
    }
    setEditing(null);
    setCreating(false);
    fetchFaqs();
  }

  async function handleDelete(id: string) {
    await supabase.from('faqs').delete().eq('id', id);
    fetchFaqs();
  }

  async function handleMove(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= faqs.length) return;
    const reordered = [...faqs];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    setFaqs(reordered);
    await Promise.all(
      reordered.map((f, i) =>
        supabase.from('faqs').update({ sort_order: i }).eq('id', f.id)
      )
    );
  }

  return (
    <AdminPage title="FAQ" description="Manage frequently asked questions for travelers and hosts.">
      <div className="flex justify-end mb-4">
        <Button size="sm" onClick={() => { setCreating(true); setEditing(null); }}>+ Add FAQ</Button>
      </div>

      {creating && (
        <div className="mb-4">
          <FaqEditor faq={EMPTY_FORM} onSave={handleSave} onCancel={() => setCreating(false)} />
        </div>
      )}

      {dataLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : faqs.length === 0 && !creating ? (
        <p className="text-center text-app-muted text-sm py-12">No FAQs yet. Create one to get started.</p>
      ) : (
        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div key={faq.id} className="rounded-xl border border-app-border bg-app-card shadow-sm overflow-hidden">
              {editing === faq.id ? (
                <div className="p-4">
                  <FaqEditor faq={faq} onSave={handleSave} onCancel={() => setEditing(null)} />
                </div>
              ) : (
                <div className="p-4 flex gap-3">
                  {/* Reorder arrows */}
                  <div className="flex flex-col shrink-0">
                    <button onClick={() => void handleMove(idx, -1)} disabled={idx === 0}
                      aria-label="Move up"
                      className="p-1 text-app-muted hover:text-app-fg disabled:opacity-25 disabled:hover:text-app-muted transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                      </svg>
                    </button>
                    <button onClick={() => void handleMove(idx, 1)} disabled={idx === faqs.length - 1}
                      aria-label="Move down"
                      className="p-1 text-app-muted hover:text-app-fg disabled:opacity-25 disabled:hover:text-app-muted transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex items-start justify-between gap-3 flex-1 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="text-sm font-semibold text-app-fg">{faq.question}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${AUDIENCE_COLORS[faq.audience]}`}>{AUDIENCE_LABELS[faq.audience]}</span>
                        {!faq.published && <StatusBadge tone="neutral" label="Draft" />}
                      </div>
                      <p className="text-sm text-app-muted whitespace-pre-wrap">{faq.answer}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="px-2 py-1 text-xs" onClick={() => { setEditing(faq.id); setCreating(false); }}>Edit</Button>
                      <Button size="sm" variant="ghost" className="px-2 py-1 text-xs text-crimson hover:text-crimson" onClick={() => void handleDelete(faq.id)}>Delete</Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminPage>
  );
}

export default AdminFAQ;
